import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '../../../environment/environment';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';

interface PriceGroupRow {
  id: number;
  label: string;
  is_active: boolean;
}

interface PriceExtraRow {
  id: number;
  label: string;
  is_active: boolean;
}

interface TemplatePriceCell {
  fixed_course_amount: number | null;
  per_participant_amount: number | null;
  extraOptionIds: number[];
}

interface CoursePriceTemplateRow {
  id: number;
  label: string;
  is_active: boolean;
  sort_order?: number;
  prices: Array<{
    price_group_id: number;
    fixed_course_amount?: number | null;
    per_participant_amount?: number | null;
    extra_option_ids?: number[];
  }>;
  extra_option_ids: number[];
}

interface TemplateDraft {
  label: string;
  pricesByGroupId: Record<number, TemplatePriceCell>;
}

@Component({
  selector: 'app-duna-event-price-templates',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDialogModule,
  ],
  templateUrl: './duna-event-price-templates.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-event-price-templates.component.scss',
})
export class DunaEventPriceTemplatesComponent implements OnInit, OnDestroy {
  priceGroups: PriceGroupRow[] = [];
  extraOptions: PriceExtraRow[] = [];
  templates: CoursePriceTemplateRow[] = [];
  draftById: Record<number, TemplateDraft> = {};
  loading = false;
  creating = false;
  savingId: number | null = null;
  togglingId: number | null = null;
  deletingId: number | null = null;
  newLabel = '';
  errorMessage: string | null = null;

  private destroy$ = new Subject<void>();
  private readonly templatesApi = `${environment.apiUrl}/events/course-price-templates`;
  private readonly groupsApi = `${environment.apiUrl}/events/price-groups`;
  private readonly extrasApi = `${environment.apiUrl}/events/price-extra-options`;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    forkJoin({
      groups: this.http.get<unknown>(this.groupsApi),
      extras: this.http.get<unknown>(this.extrasApi, {
        params: { activeOnly: 'true' },
      }),
      templates: this.http.get<unknown>(this.templatesApi),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: ({ groups, extras, templates }) => {
          this.priceGroups = this.parseGroups(groups).filter(
            (g) => g.is_active
          );
          this.extraOptions = this.parseExtras(extras).filter(
            (e) => e.is_active
          );
          this.templates = this.parseTemplates(templates).sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
          );
          this.syncDrafts();
        },
        error: (error) => {
          this.priceGroups = [];
          this.extraOptions = [];
          this.templates = [];
          this.draftById = {};
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke hente prisskabeloner.'
          );
        },
      });
  }

  private parseItems(res: unknown): unknown[] {
    if (Array.isArray(res)) return res;
    if (res && typeof res === 'object') {
      const o = res as Record<string, unknown>;
      const inner = o['items'] ?? o['data'] ?? o['results'];
      if (Array.isArray(inner)) return inner;
    }
    return [];
  }

  private parseGroups(res: unknown): PriceGroupRow[] {
    return this.parseItems(res)
      .map((raw) => {
        const o = raw as Record<string, unknown>;
        const id = Number(o['id']);
        if (!Number.isFinite(id) || id <= 0) return null;
        return {
          id,
          label: String(o['label'] ?? ''),
          is_active: o['is_active'] !== false,
        };
      })
      .filter((x): x is PriceGroupRow => x != null);
  }

  private parseExtras(res: unknown): PriceExtraRow[] {
    return this.parseGroups(res);
  }

  private parseExtraIds(raw: unknown): number[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  private parseTemplates(res: unknown): CoursePriceTemplateRow[] {
    return this.parseItems(res).flatMap((raw): CoursePriceTemplateRow[] => {
      const o = raw as Record<string, unknown>;
      const id = Number(o['id']);
      if (!Number.isFinite(id) || id <= 0) {
        return [];
      }
      const legacyExtraIds = this.parseExtraIds(o['extra_option_ids']);
      const prices = Array.isArray(o['prices'])
        ? (o['prices'] as Array<Record<string, unknown>>)
        : [];
      return [
        {
          id,
          label: String(o['label'] ?? ''),
          is_active: o['is_active'] !== false,
          sort_order: Number(o['sort_order'] ?? 0),
          prices: prices.map((p) => ({
            price_group_id: Number(p['price_group_id']),
            fixed_course_amount:
              p['fixed_course_amount'] != null
                ? Number(p['fixed_course_amount'])
                : null,
            per_participant_amount:
              p['per_participant_amount'] != null
                ? Number(p['per_participant_amount'])
                : null,
            extra_option_ids: this.parseExtraIds(p['extra_option_ids']),
          })),
          extra_option_ids: legacyExtraIds,
        },
      ];
    });
  }

  private emptyPriceCell(): TemplatePriceCell {
    return {
      fixed_course_amount: null,
      per_participant_amount: null,
      extraOptionIds: [],
    };
  }

  private extraIdsForPriceRow(
    row: CoursePriceTemplateRow,
    priceGroupId: number
  ): number[] {
    const priceRow = row.prices.find((p) => p.price_group_id === priceGroupId);
    if (priceRow?.extra_option_ids && priceRow.extra_option_ids.length > 0) {
      return [...priceRow.extra_option_ids];
    }
    return [...row.extra_option_ids];
  }

  private buildDraftFromRow(row: CoursePriceTemplateRow): TemplateDraft {
    const pricesByGroupId: Record<number, TemplatePriceCell> = {};
    for (const g of this.priceGroups) {
      pricesByGroupId[g.id] = {
        ...this.emptyPriceCell(),
        extraOptionIds: this.extraIdsForPriceRow(row, g.id),
      };
    }
    for (const p of row.prices) {
      if (!pricesByGroupId[p.price_group_id]) {
        pricesByGroupId[p.price_group_id] = this.emptyPriceCell();
      }
      pricesByGroupId[p.price_group_id] = {
        fixed_course_amount: p.fixed_course_amount ?? null,
        per_participant_amount: p.per_participant_amount ?? null,
        extraOptionIds: this.extraIdsForPriceRow(row, p.price_group_id),
      };
    }
    return {
      label: row.label,
      pricesByGroupId,
    };
  }

  private syncDrafts(): void {
    const next: Record<number, TemplateDraft> = {};
    this.templates.forEach((r) => {
      next[r.id] = this.buildDraftFromRow(r);
    });
    this.draftById = next;
  }

  private extraIdsEqual(a: number[], b: number[]): boolean {
    return (
      [...a].sort((x, y) => x - y).join(',') ===
      [...b].sort((x, y) => x - y).join(',')
    );
  }

  rowDirty(row: CoursePriceTemplateRow): boolean {
    const draft = this.draftById[row.id];
    if (!draft) return false;
    if (draft.label.trim() !== (row.label || '').trim()) return true;
    for (const g of this.priceGroups) {
      const saved = row.prices.find((p) => p.price_group_id === g.id);
      const cell = draft.pricesByGroupId[g.id];
      const savedFixed = saved?.fixed_course_amount ?? null;
      const savedPer = saved?.per_participant_amount ?? null;
      if (
        Number(cell?.fixed_course_amount ?? null) !== Number(savedFixed ?? null)
      )
        return true;
      if (
        Number(cell?.per_participant_amount ?? null) !==
        Number(savedPer ?? null)
      )
        return true;
      const savedExtras = this.extraIdsForPriceRow(row, g.id);
      if (!this.extraIdsEqual(cell?.extraOptionIds ?? [], savedExtras)) {
        return true;
      }
    }
    return false;
  }

  isExtraSelected(
    rowId: number,
    groupId: number,
    extraId: number
  ): boolean {
    return (
      this.draftById[rowId]?.pricesByGroupId[groupId]?.extraOptionIds ?? []
    ).includes(extraId);
  }

  toggleExtra(
    rowId: number,
    groupId: number,
    extraId: number,
    checked: boolean
  ): void {
    const cell = this.draftById[rowId]?.pricesByGroupId[groupId];
    if (!cell) return;
    const set = new Set(cell.extraOptionIds);
    if (checked) set.add(extraId);
    else set.delete(extraId);
    cell.extraOptionIds = Array.from(set);
  }

  private buildPricesPayload(
    draft: TemplateDraft
  ): Array<Record<string, unknown>> {
    return this.priceGroups.map((g) => {
      const cell = draft.pricesByGroupId[g.id] ?? this.emptyPriceCell();
      return {
        price_group_id: g.id,
        fixed_course_amount: cell.fixed_course_amount,
        per_participant_amount: cell.per_participant_amount,
        extra_option_ids: [...cell.extraOptionIds],
      };
    });
  }

  saveRow(row: CoursePriceTemplateRow): void {
    const draft = this.draftById[row.id];
    if (!draft) return;
    const label = draft.label.trim();
    if (!label) {
      this.errorMessage = 'Tekst må ikke være tom.';
      return;
    }
    if (!this.rowDirty(row)) return;

    this.savingId = row.id;
    this.errorMessage = null;
    this.http
      .patch<unknown>(`${this.templatesApi}/${row.id}`, {
        label,
        prices: this.buildPricesPayload(draft),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.savingId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.load(),
        error: (error) => {
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke opdatere prisskabelon.'
          );
        },
      });
  }

  onActiveChange(row: CoursePriceTemplateRow, ev: MatSlideToggleChange): void {
    const next = ev.checked;
    this.togglingId = row.id;
    this.errorMessage = null;
    this.http
      .patch<unknown>(`${this.templatesApi}/${row.id}`, { is_active: next })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.togglingId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.load(),
        error: (error) => {
          ev.source.checked = !next;
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke ændre aktiv status.'
          );
        },
      });
  }

  createTemplate(): void {
    const label = this.newLabel.trim();
    if (!label) {
      this.errorMessage = 'Angiv en tekst for den nye prisskabelon.';
      return;
    }
    this.creating = true;
    this.errorMessage = null;
    this.http
      .post<unknown>(this.templatesApi, { label })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.creating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.newLabel = '';
          this.load();
        },
        error: (error) => {
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke oprette prisskabelon.'
          );
        },
      });
  }

  deleteTemplate(row: CoursePriceTemplateRow): void {
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      width: 'min(520px, 92vw)',
      data: {
        title: 'Slet prisskabelon?',
        message: `Vil du slette «${row.label}» fra kataloget?`,
        details: [
          'Eksisterende events påvirkes ikke.',
          'Deaktiver i stedet, hvis skabelonen ikke skal vises i add/edit-event.',
        ],
        confirmText: 'Ja, slet',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });
    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.deletingId = row.id;
        this.errorMessage = null;
        this.http
          .delete(`${this.templatesApi}/${row.id}`)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => {
              this.deletingId = null;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: () => {
              this.templates = this.templates.filter((x) => x.id !== row.id);
              delete this.draftById[row.id];
            },
            error: (error) => {
              this.errorMessage = this.formatApiError(
                error,
                'Kunne ikke slette prisskabelon.'
              );
            },
          });
      });
  }

  private formatApiError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown; message?: string };
    const body = httpErr?.error;
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object') {
          return this.formatApiError({ ...httpErr, error: parsed }, fallback);
        }
      } catch {
        return body.trim() || fallback;
      }
    }
    if (body && typeof body === 'object') {
      const o = body as { message?: string; error?: string; detail?: string };
      const tech = (o.detail || o.error || '').trim();
      const msg = (o.message || '').trim();
      if (tech && msg) return `${msg}: ${tech}`;
      return tech || msg || fallback;
    }
    return (httpErr?.message || '').trim() || fallback;
  }
}
